import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { useStandUpDashboard } from '../hooks/useStandUpDashBoard';
import { 
  fetchFavorites,
  setTaskToFavoriteIdMapping,
  removeTaskToFavoriteIdMapping,
  type FavoriteItem
} from '../../../store/slices/standupRedirectSlice';
import { setToastNotification } from '../../../store/slices/standUpSlice';
import { createApiClient } from '../../../api/apiClient';
import { apiClient } from '../../../api/apiClient';
import logger from '../../../utils/logger';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';

interface UseFavoritesParams {
  standupSessionId?: string;
  standupId?: string;
  standupType?: string;
  isViewMode?: boolean;
  selectedLocation?: {
    id: string;
    displayText: string;
  };
  isEditMode?: boolean;
}

/**
 * Custom hook to manage favorites (used in both Review Performance and Key Activities tabs)
 * 
 * FLOW:
 * 1. User selects standup type & date in StandUpScreen → clicks Continue
 * 2. StandUpScreen generates session ID and redirects to StandUpRedirectScreen
 * 3. StandUpRedirectScreen calls this hook with session ID, type, and location
 * 4. Hook fetches favorites via GET /stand-up/favourites and stores in Redux
 * 5. Both tabs (Review Performance & Key Activities) access favorites from Redux
 * 
 * API ENDPOINTS:
 * - GET /stand-up/favourites: Fetch all favorites for session
 * - POST /stand-up/favourites: Add new favorite
 * - PUT /stand-up/favourites: Update favorite (mark as favorite/unfavorite)
 * 
 * Fetches favorites only when session/location/type changes, not on every render
 * Stores data in Redux for global access across tabs
 */
export const useFavorites = ({ 
  standupSessionId, 
  standupId,
  standupType, 
  selectedLocation,
  isEditMode = false,
  isViewMode = false
}: UseFavoritesParams = {}) => {
  const dispatch = useDispatch<AppDispatch>();

  const {transformStandupTypeForFavourites} = useStandUpDashboard();
  
  // Get user email from Redux
  const userEmail = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? ''
  );
  
  // Get favorites data from Redux
  const favoritesState = useSelector(
    (state: RootState) => state.standupRedirect.favorites
  );

  // Fetch favorites when required parameters change
  useEffect(() => {
    console.log('=== useFavorites useEffect triggered ===');
    console.log('Parameters:', {
      standupSessionId,
      standupId,
      standupType,
      selectedLocationId: selectedLocation?.id,
      selectedLocationName: selectedLocation?.displayText,
      isEditMode,
      isViewMode,
    });

    // Only fetch if:
    // 1. We have all required parameters
    // 2. Parameters have changed from last fetch
    if (
      standupSessionId &&
      standupType &&
      selectedLocation?.id &&
      selectedLocation?.displayText
    ) {
      const lastParams = favoritesState.lastFetchedParams;
      const paramsChanged =
        !lastParams ||
        lastParams.sessionId !== standupSessionId ||
        lastParams.locationId !== selectedLocation.id ||
        lastParams.standupType !== standupType;

      console.log('Parameters check passed. paramsChanged:', paramsChanged);
      console.log('Last fetched params:', lastParams);
      console.log('Current params for comparison:', {
        sessionId: standupSessionId,
        locationId: selectedLocation.id,
        standupType: standupType,
      });

      if (paramsChanged) {
        console.log('Dispatching fetchFavorites with params:', {
          standupSessionId,
          standupId,
          isEditMode,
          isViewMode,
          standupType,
          locationId: selectedLocation.id,
          locationName: selectedLocation.displayText,
        });

        dispatch(
          fetchFavorites({
            standupSessionId,
            standupId,
            locationId: selectedLocation.id,
            locationName: selectedLocation.displayText,
            standupType: transformStandupTypeForFavourites(String(standupType)),
            isEditMode,
            isViewMode,
          })
        );
      } else {
        console.log('Parameters unchanged, skipping fetchFavorites');
      }
    } else {
      console.log('Required parameters missing, skipping fetchFavorites:', {
        hasStandupSessionId: !!standupSessionId,
        hasStandupType: !!standupType,
        hasSelectedLocationId: !!selectedLocation?.id,
        hasSelectedLocationDisplayText: !!selectedLocation?.displayText,
      });
    }
    console.log('=== useFavorites useEffect end ===');
  }, [
    standupSessionId,
    standupId,
    standupType,
    selectedLocation?.id,
    selectedLocation?.displayText,
    isEditMode,
    isViewMode,
    // Note: Intentionally excluding dispatch (stable) and favoritesState.lastFetchedParams (to avoid reference cycles)
  ]);

  /**
   * Get KFA favorites (for Key Activities tab)
   * Memoized to prevent recalculation on every render
   */
  const kfaFavorites = useMemo((): FavoriteItem[] => {
    console.log('=== kfaFavorites CALCULATION ===');
    console.log('favoritesState.data:', favoritesState.data);
    console.log('favoritesState.data type:', typeof favoritesState.data);
    console.log('favoritesState.data isArray:', Array.isArray(favoritesState.data));
    
    // Safety check: ensure data is an array before filtering
    if (!Array.isArray(favoritesState.data)) {
      console.warn('favoritesState.data is not an array:', favoritesState.data);
      return [];
    }
    
    const filtered = favoritesState.data.filter((item) => {
      console.log('Filtering item:', item, 'item_type:', item.item_type, 'is_favourite:', item.is_favourite);
      // Only include KFA items that are actively favorited (is_favourite: true)
      return (item.item_type === 'kfa' || item.item_type === 'KFA') && item.is_favourite === true;
    });
    
    console.log('Filtered KFA favorites:', filtered);
    console.log('===============================');
    return filtered;
  }, [favoritesState.data]);

  /**
   * Add a favorite to the backend and update Redux mapping
   * 
   * API: POST /stand-up/favourites
   * Body: {
   *   standup_session_id: string,
   *   location_id: string,
   *   location_name: string,
   *   standup_type: string,
   *   item_type: "kfa" | "kpi" | "process",
   *   item_id: string,
   *   item_name: string,
   *   module_name: string,
   *   created_by: string (user email)
   * }
   */
  const addFavorite = async (
    taskId: string,
    kfaId: string,
    itemName: string,
    moduleName: string
  ): Promise<boolean> => {
    if (!standupSessionId || !standupType || !selectedLocation || !userEmail) {
      console.error('Missing required parameters for adding favorite');
      return false;
    }

    try {
      const api = createApiClient();
      // When in edit mode: include both standup_id and standup_session_id
      // When in view mode: include only standup_id
      // When new standup: include only standup_session_id
      const requestBody = isEditMode ? {
        standup_id: standupId,
        standup_session_id: standupSessionId,
        location_id: selectedLocation.id.toString(),
        location_name: selectedLocation.displayText,
       standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: 'kfa',
        item_id: kfaId,
        item_name: itemName,
        module_name: moduleName,
        created_by: userEmail,
      } : isViewMode ? {
        standup_id: standupId,
        standup_session_id: standupSessionId,
        location_id: selectedLocation.id.toString(),
        location_name: selectedLocation.displayText,
        standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: 'kfa',
        item_id: kfaId,
        item_name: itemName,
        module_name: moduleName,
        created_by: userEmail,
      } : {
        standup_session_id: standupSessionId,
        location_id: selectedLocation.id.toString(),
        location_name: selectedLocation.displayText,
        standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: 'kfa',
        item_id: kfaId,
        item_name: itemName,
        module_name: moduleName,
        created_by: userEmail,
      };

      console.log('POST /stand-up/favourites with body:', requestBody);

      const response = await apiClient(api, '/stand-up/favourites', 'POST', requestBody);

      console.log('POST /stand-up/favourites full response:', JSON.stringify(response, null, 2));

      // Try to get favourite_id from different possible locations in the response
      let favouriteId = response.data?.favourite_id || 
                        response.data?.favoriteId || 
                        response.favourite_id || 
                        response.favoriteId ||
                        response.data?.data?.favourite_id ||
                        response.data?.data?.favoriteId;
      
      // If response.data is the favorite object itself, try to get id from it
      if (!favouriteId && typeof response.data === 'object' && response.data !== null) {
        favouriteId = response.data.id;
      }
      
      console.log('Extracted favouriteId:', favouriteId);
      
      if (response.success && favouriteId) {
        // Store the mapping in Redux
        console.log('Storing mapping - taskId:', taskId, 'favouriteId:', favouriteId);
        dispatch(
          setTaskToFavoriteIdMapping({
            taskId,
            favoriteId: favouriteId,
          })
        );
        console.log('✓ Successfully added favorite with favourite_id:', favouriteId);
        
        // Refetch favorites to update the Redux state with the latest data
        // This ensures the favorites list is in sync
        dispatch(
          fetchFavorites({
            standupSessionId,
            standupId,
            locationId: selectedLocation.id,
            locationName: selectedLocation.displayText,
            standupType: transformStandupTypeForFavourites(String(standupType)),
            isEditMode,
            isViewMode,
          })
        );
        
        return true;
      } else {
        console.error('✗ Failed to add favorite or favourite_id missing:', response);
        console.error('response.success:', response.success);
        console.error('response.data:', response.data);
        console.error('Tried to find favourite_id at multiple locations but not found');
        
        // Use centralized error handler to show overlay/modal
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.ADD_FAVORITE,
          new Error(response?.message || 'Failed to add favorite. Please try again.'),
          true, // displayModal = true → show overlay
          { taskId, kfaId, itemName }
        );
        return false;
      }
    } catch (error: any) {
      console.error('✗ Error adding favorite:', error);
      
      // Use centralized error handler for network/unexpected errors
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.ADD_FAVORITE,
        error,
        true, // displayModal = true → show overlay
        { taskId, action: 'add_favorite', kfaId }
      );
      return false;
    }
  };

  /**
   * Remove a favorite from the backend and update Redux mapping
   * 
   * API: PUT /stand-up/favourites
   * Body: {
   *   standup_session_id: string,
   *   location_id: string,
   *   location_name: string,
   *   standup_type: string,
   *   item_type: "kfa",
   *   item_id: string,
   *   item_name: string,
   *   module_name: string,
   *   is_favourite: false,
   *   modified_by: string (user email)
   * }
   */
  const removeFavorite = async (
    taskId: string,
    kfaId: string = '',
    itemName: string = '',
    moduleName: string = ''
  ): Promise<boolean> => {
    if (!standupSessionId || !standupType || !selectedLocation || !userEmail) {
      console.error('Missing required parameters for removing favorite');
      return false;
    }

    // Use provided parameters or empty string as fallback
    const finalKfaId = kfaId || '';
    const finalItemName = itemName || '';
    const finalModuleName = moduleName || '';

    try {
      const api = createApiClient();
      // Same payload structure as addFavorite, but with is_favourite: false
      const requestBody = isEditMode ? {
        standup_id: standupId,
        standup_session_id: standupSessionId,
        location_id: selectedLocation.id.toString(),
        location_name: selectedLocation.displayText,
        standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: 'kfa',
        item_id: finalKfaId,
        item_name: finalItemName,
        module_name: finalModuleName,
        is_favourite: false,
        modified_by: userEmail,
      } : isViewMode ? {
        standup_id: standupId,
        standup_session_id: standupSessionId,
        location_id: selectedLocation.id.toString(),
        location_name: selectedLocation.displayText,
        standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: 'kfa',
        item_id: finalKfaId,
        item_name: finalItemName,
        module_name: finalModuleName,
        is_favourite: false,
        modified_by: userEmail,
      } : {
        standup_session_id: standupSessionId,
        location_id: selectedLocation.id.toString(),
        location_name: selectedLocation.displayText,
        standup_type:  transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: 'kfa',
        item_id: finalKfaId,
        item_name: finalItemName,
        module_name: finalModuleName,
        is_favourite: false,
        modified_by: userEmail,
      };

      console.log('PUT /stand-up/favourites with body:', requestBody);

      const response = await apiClient(api, '/stand-up/favourites', 'PUT', requestBody);

      console.log('PUT /stand-up/favourites response:', response);

      if (response.success) {
        // Remove from Redux mapping
        dispatch(removeTaskToFavoriteIdMapping(taskId));
        console.log('✓ Successfully removed favorite');
        
        // Refetch favorites to update the Redux state with the latest data
        dispatch(
          fetchFavorites({
            standupSessionId,
            standupId,
            locationId: selectedLocation.id,
            locationName: selectedLocation.displayText,
            standupType: transformStandupTypeForFavourites(String(standupType)),
            isEditMode,
            isViewMode,
          })
        );
        
        return true;
      } else {
        console.error('✗ Failed to remove favorite:', response);
        // Use centralized error handler to show overlay/modal
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.REMOVE_FAVORITE,
          new Error(response?.message || 'Failed to remove favorite'),
          true, // displayModal = true → show overlay
          { taskId, finalKfaId, finalItemName }
        );
        return false;
      }
    } catch (error: any) {
      console.error('✗ Error removing favorite:', error);
      // Use centralized error handler for network/unexpected errors
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.REMOVE_FAVORITE,
        error,
        true, // displayModal = true → show overlay
        { taskId, action: 'remove_favorite' }
      );
      return false;
    }
  };

  /**
   * Update a favorite (mark as favorite or unfavorite)
   * 
   * API: PUT /stand-up/favourites
   * Body: {
   *   favourite_id: string,
   *   standup_id: string,
   *   is_favourite: boolean,
   *   modified_by: string (user email)
   * }
   */
  const updateFavorite = async (
    taskId: string,
    isFavorite: boolean
  ): Promise<boolean> => {
    const favoriteId = favoritesState.taskToFavoriteIdMap[taskId];

    if (!favoriteId || !userEmail || !standupSessionId) {
      console.warn('Missing favorite_id, userEmail, or standupSessionId for updating favorite');
      return false;
    }

    // Find the favorite item to get additional details
    const favoriteItem = Array.isArray(favoritesState.data)
      ? favoritesState.data.find(fav => fav.favourite_id === favoriteId)
      : undefined;

    try {
      const api = createApiClient();
      // When in edit mode: include both standup_id and standup_session_id
      // When in view mode: include only standup_id
      // When new standup: include only standup_id (same as view)
      const requestBody = isEditMode ? {
        favourite_id: favoriteId.toString(),
        standup_id: standupId,
        standup_session_id: standupSessionId,
        is_favourite: isFavorite,
        modified_by: userEmail,
      } : {
        favourite_id: favoriteId.toString(),
        standup_id: standupSessionId,
        location_id: selectedLocation?.id?.toString() || '',
        location_name: selectedLocation?.displayText || '',
        standup_type: transformStandupTypeForFavourites(String(standupType)) || '',
        item_type: favoriteItem?.item_type || 'kfa',
        item_id: favoriteItem?.item_id || '',
        item_name: favoriteItem?.item_name || '',
        module_name: favoriteItem?.item_name || '',
        is_favourite: isFavorite,
        modified_by: userEmail,
      };

      console.log('PUT /stand-up/favourites with body:', requestBody);

      const response = await apiClient(api, '/stand-up/favourites', 'PUT', requestBody);

      if (response.success) {
        if (!isFavorite) {
          // Remove from Redux mapping if unfavoriting
          dispatch(removeTaskToFavoriteIdMapping(taskId));
        }
        console.log(`✓ Successfully ${isFavorite ? 'favorited' : 'unfavorited'} item`);
        return true;
      } else {
        console.error('✗ Failed to update favorite:', response);
        // Use centralized error handler to show overlay/modal
        handleStandUpErrorWithFriendlyMessage(
          isFavorite ? STANDUP_API_NAMES.ADD_FAVORITE : STANDUP_API_NAMES.REMOVE_FAVORITE,
          new Error(response?.message || 'Failed to update favorite'),
          true, // displayModal = true → show overlay
          { taskId, favoriteId, isFavorite }
        );
        return false;
      }
    } catch (error: any) {
      console.error('✗ Error updating favorite:', error);
      // Use centralized error handler for network/unexpected errors
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.UPDATE_FAVORITE,
        error,
        true, // displayModal = true → show overlay
        { taskId, action: 'update_favorite' }
      );
      return false;
    }
  };

  /**
   * Get favorite ID for a task
   */
  const getFavoriteIdForTask = (taskId: string): number | undefined => {
    return favoritesState.taskToFavoriteIdMap[taskId];
  };

  const returnData = {
    // State
    favorites: Array.isArray(favoritesState.data) ? favoritesState.data : [],
    kfaFavorites,
    loading: favoritesState.loading,
    error: favoritesState.error,
    taskToFavoriteIdMap: favoritesState.taskToFavoriteIdMap,
    
    // Actions
    addFavorite,
    removeFavorite,
    updateFavorite,
    getFavoriteIdForTask,
  };

  console.log('=== useFavorites RETURN DATA ===');
  console.log('Returning favorites:', returnData.favorites);
  console.log('Returning kfaFavorites:', returnData.kfaFavorites);
  console.log('Returning loading:', returnData.loading);
  console.log('===============================');

  return returnData;
};
